/**
 * Target campaigns list
 *
 * Campaigns picked to join a pool, each flagged when its currency differs from the pool's.
 */

import React from 'react';
import { Button, Table } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { i18nKeys } from '../constants';
import { isCurrencyMismatch } from '../helpers';
import { CampaignSummary } from '../types';
import Styles from '../GiftMatchingPools.module.css';

interface TargetCampaignsListProps {
  campaigns: CampaignSummary[];
  onRemove: (campaignId: number) => void;
  poolCurrency: string | null;
}

const TargetCampaignsList: React.FC<TargetCampaignsListProps> = ({
  campaigns,
  onRemove,
  poolCurrency
}) => {
  if (campaigns.length === 0) {
    return null;
  }
  return (
    <Table celled compact>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            <Translate value={i18nKeys.CAMPAIGNS.CAMPAIGN} />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <Translate value={i18nKeys.CAMPAIGNS.ORGANISATION} />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <Translate value={i18nKeys.CAMPAIGNS.CURRENCY} />
          </Table.HeaderCell>
          <Table.HeaderCell>
            <Translate value={i18nKeys.CAMPAIGNS.ACTIONS} />
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {campaigns.map((campaign) => {
          const mismatch = isCurrencyMismatch(campaign.currency, poolCurrency);
          return (
            <Table.Row key={campaign.campaignId} error={mismatch}>
              <Table.Cell>
                {campaign.campaignName} ({campaign.campaignId})
              </Table.Cell>
              <Table.Cell>{campaign.organizationName}</Table.Cell>
              <Table.Cell>
                {campaign.currency}
                {mismatch && (
                  <div className={Styles.cellError} role="alert">
                    {I18n.t(i18nKeys.WARNINGS.CURRENCY, {
                      currency: campaign.currency,
                      poolCurrency
                    })}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell>
                <Button
                  type="button"
                  basic
                  size="tiny"
                  aria-label={`${I18n.t(i18nKeys.CAMPAIGNS.REMOVE_FROM_LIST)} ${campaign.campaignName ?? campaign.campaignId}`}
                  onClick={() => onRemove(campaign.campaignId)}
                >
                  <Translate value={i18nKeys.CAMPAIGNS.REMOVE_FROM_LIST} />
                </Button>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  );
};

export default TargetCampaignsList;
